const ICON_ARROW = `
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none">
    <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="var(--icon-stroke-width)" vector-effect="non-scaling-stroke" d="M4.25 10h11.5m0 0-4-4m4 4-4 4"></path>
  </svg>
`;

const ICON_CLOSE = `
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2L2 12" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"></path>
    <path d="M12 12L2 2" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"></path>
  </svg>
`;

class ClassCardComponent extends HTMLElement {
  connectedCallback() {
    this.article = this.querySelector('.class-card');
    this.classData = this.#parseDataAttribute();
    this.isRegistrationMode = this.dataset.registrationMode === 'true';

    console.log('[ClassCard] connectedCallback', {
      classId: this.dataset.classId,
      isRegistrationMode: this.isRegistrationMode,
      hasArticle: !!this.article,
      hasData: !!this.classData,
    });

    if (!this.article || !this.classData) {
      console.warn('[ClassCard] Missing article or classData');
      return;
    }

    // Only add click handlers if NOT in registration mode
    // In registration mode, the registration manager handles button clicks via event delegation
    if (!this.isRegistrationMode) {
      console.log('[ClassCard] Adding click handlers for normal mode');
      this.article.addEventListener('click', this.#handleActivate);
      this.article.addEventListener('keydown', this.#handleKeydown);
    } else {
      console.log('[ClassCard] Registration mode - adding image click handler only');
      // In registration mode, only the image should open the modal
      this.mediaElement = this.article.querySelector('.class-card__media');
      if (this.mediaElement) {
        this.mediaElement.addEventListener('click', this.#handleMediaClick);
        this.mediaElement.style.cursor = 'pointer';
      }

      // Update button state if already registered
      this.#updateCardButtonState();

      // Listen for registration updates to refresh button state
      document.addEventListener('registration:updated', this.#handleRegistrationUpdate);
      document.addEventListener('registration:synced', this.#handleRegistrationUpdate);
      document.addEventListener('selection:changed', this.#handleSelectionChange);
    }
  }

  disconnectedCallback() {
    if (this.article && !this.isRegistrationMode) {
      this.article.removeEventListener('click', this.#handleActivate);
      this.article.removeEventListener('keydown', this.#handleKeydown);
    }
    if (this.mediaElement && this.isRegistrationMode) {
      this.mediaElement.removeEventListener('click', this.#handleMediaClick);
    }
    if (this.isRegistrationMode) {
      document.removeEventListener('registration:updated', this.#handleRegistrationUpdate);
      document.removeEventListener('registration:synced', this.#handleRegistrationUpdate);
      document.removeEventListener('selection:changed', this.#handleSelectionChange);
    }
  }

  #handleActivate = (event) => {
    console.log('[ClassCard] Article clicked - opening dialog');
    event.preventDefault();
    this.#openDialog();
  };

  #handleKeydown = (event) => {
    if (event.defaultPrevented) return;
    if (event.key === 'Enter' || event.key === ' ') {
      console.log('[ClassCard] Keyboard activation - opening dialog');
      event.preventDefault();
      this.#openDialog();
    }
  };

  #handleMediaClick = (event) => {
    console.log('[ClassCard] Media element clicked in registration mode - opening dialog');
    event.preventDefault();
    event.stopPropagation();
    this.#openDialog();
  };

  #handleRegistrationUpdate = () => {
    this.#updateCardButtonState();
  };

  #handleSelectionChange = (event) => {
    // Only update if this event is for this card's class
    if (event.detail?.classId === this.dataset.classId) {
      this.#updateCardButtonState();
    }
  };

  /**
   * Updates the card button to show registered/selected state if applicable
   */
  #updateCardButtonState() {
    const button = this.article?.querySelector('.class-card__register-button');
    if (!button) return;

    const textEl = button.querySelector('.class-card__register-button-text');
    const isRegistered = this.#isClassRegistered();
    const isClassPast = this.dataset.classPast === 'true';

    if (isRegistered) {
      button.classList.add('class-card__register-button--registered');
      button.classList.remove('class-card__register-button--selected');
      button.disabled = true;
      if (textEl) {
        textEl.textContent = 'Registered';
      }
    } else if (isClassPast) {
      button.classList.add('class-card__register-button--past');
      button.classList.remove('class-card__register-button--selected');
      button.classList.remove('class-card__register-button--registered');
      button.disabled = true;
      if (textEl) {
        textEl.textContent = 'Class Ended';
      }
    } else {
      button.classList.remove('class-card__register-button--registered');
      button.disabled = false;

      // Check if pending selection (checkbox checked but not yet committed)
      const checkboxId = this.dataset.classId?.replace('://', '-').replace(/\//g, '-');
      const checkbox = checkboxId ? document.getElementById(`class-${checkboxId}`) : null;

      if (checkbox?.checked) {
        button.classList.add('class-card__register-button--selected');
        if (textEl) {
          textEl.textContent = 'Selected';
        }
      } else {
        button.classList.remove('class-card__register-button--selected');
        if (textEl) {
          textEl.textContent = 'Select to Register';
        }
      }
    }
  }

  #preloadModalMedia() {
    if (this._preloadedMedia) return;
    this._preloadedMedia = true;
    const media = this.classData?.media;
    if (!Array.isArray(media) || !media.length) return;
    const first = media[0];
    if (!first || !first.url) return;
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = first.url;
    if (first.srcset) {
      link.setAttribute('imagesrcset', first.srcset);
      link.setAttribute('imagesizes', '(min-width: 900px) 480px, 92vw');
    }
    link.fetchPriority = 'high';
    document.head.appendChild(link);
  }

  async #openDialog() {
    console.log('[ClassCard] Opening dialog');
    this.#preloadModalMedia();
    if (!this.dialogComponent) {
      this.#createDialog();
    }

    if (!this.dialogComponent || !this.dialog) {
      console.warn('[ClassCard] Missing dialogComponent or dialog after create');
      return;
    }

    console.log('[ClassCard] dialog.showModal available:', typeof this.dialog.showModal === 'function');
    console.log('[ClassCard] HTMLDialogElement supported:', typeof HTMLDialogElement !== 'undefined');

    // Fallback for browsers without native <dialog> support (iOS < 15.4)
    if (typeof this.dialog.showModal !== 'function') {
      console.log('[ClassCard] Using fallback dialog (no showModal support)');
      this.#showDialogFallback();
      return;
    }

    await customElements.whenDefined('dialog-component');
    console.log('[ClassCard] dialog-component defined, calling showDialog');
    if (typeof this.dialogComponent.showDialog === 'function') {
      this.dialogComponent.showDialog();
    } else {
      this.dialog.showModal();
    }
  }

  #showDialogFallback() {
    console.log('[ClassCard] Fallback: opening dialog with setAttribute');
    this.dialog.setAttribute('open', '');
    this.dialog.classList.add('class-card-modal--fallback');
    this._fallbackScrollY = window.scrollY;
    document.body.style.width = '100%';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this._fallbackScrollY}px`;
    document.documentElement.setAttribute('scroll-lock', '');
  }

  #closeDialogFallback() {
    console.log('[ClassCard] Fallback: closing dialog');
    this.dialog.removeAttribute('open');
    this.dialog.classList.remove('class-card-modal--fallback');
    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo(0, this._fallbackScrollY || 0);
    document.documentElement.removeAttribute('scroll-lock');
  }

  #parseDataAttribute() {
    const { classData } = this.dataset;
    if (!classData) return null;

    try {
      return JSON.parse(classData);
    } catch (error) {
      console.error('class-card-component: unable to parse data', error);
      return null;
    }
  }

  #createDialog() {
    const data = this.classData || {};
    const {
      modal_summary_text: modalSummaryText,
      media = [],
      instructor_bio_html: instructorBioHTMLRaw,
      instructor_bio: instructorBio,
      class_description_html: classDescriptionHTMLRaw,
      class_description: classDescriptionPlain,
      instructor_name: instructorName,
      instructor_image: instructorImage,
      status_text: statusTextRaw,
      label_text: labelText,
      status_color: statusColorRaw,
      label_color: labelColor,
      class_name: className,
      date,
      time,
      room,
      category,
      availability_message: availabilityMessageRaw,
      registrants_limit: registrantsLimit,
      registrants_count: registrantsCount,
      requires_kit: requiresKit,
      kit_product_title: kitProductTitle,
      kit_product_url: kitProductUrl,
      kit_product_price: kitProductPrice,
      kit_product_description: kitProductDescription,
      color_scheme: colorScheme = 'scheme-1',
      placeholder_svg: placeholderSVG,
    } = data;

    const statusText = statusTextRaw || labelText || '';
    const statusColor = statusColorRaw || labelColor || '';
    const instructorBioHTML = instructorBioHTMLRaw || (instructorBio ? `<p>${this.#escapeHtml(instructorBio)}</p>` : '');
    const classDescriptionHTML = classDescriptionHTMLRaw || (classDescriptionPlain ? `<p>${this.#escapeHtml(classDescriptionPlain)}</p>` : '');

    const registrantsCountNumber = this.#toNumber(registrantsCount);
    const registrantsLimitNumber = this.#toNumber(registrantsLimit);
    const availabilityMessage = availabilityMessageRaw || this.#buildAvailabilityMessage(registrantsCountNumber, registrantsLimitNumber);

    const modalId = this.dataset.modalId || `class-modal-${Math.random().toString(36).slice(2)}`;
    const modalTitleId = this.dataset.modalTitleId || `${modalId}-title`;
    const modalSummaryId = this.dataset.modalSummaryId || `${modalId}-summary`;
    const previousLabel = this.dataset.previousLabel || 'Previous slide';
    const nextLabel = this.dataset.nextLabel || 'Next slide';
    const closeLabel = this.dataset.closeLabel || 'Close dialog';
    const kitLabel = this.dataset.kitLabel || 'Kit';
    const kitNote = this.dataset.kitNote || 'A kit is recommended for this class.';
    const kitsClosedMessage = this.dataset.kitsClosedMessage || 'Online kit sales have closed. Kits are still purchasable at the show.';
    const kitPurchaseNote = this.dataset.kitPurchaseNote || 'Class kits can be purchased from this website at any time or later at the show.';
    const instructorLabel = this.dataset.instructorLabel || 'Instructor';
    const dateLabel = this.dataset.dateLabel || 'Date';
    const timeLabel = this.dataset.timeLabel || 'Time';
    const locationLabel = this.dataset.locationLabel || 'Location';
    const categoryLabel = this.dataset.categoryLabel || 'Category';
    const availabilityLabel = this.dataset.availabilityLabel || 'Availability';

    const dialogComponent = document.createElement('dialog-component');
    dialogComponent.classList.add('class-card-modal-host');

    const dialog = document.createElement('dialog');
    dialog.id = modalId;
    dialog.className = `class-card-modal dialog-modal color-${colorScheme}`;
    dialog.setAttribute('ref', 'dialog');
    dialog.setAttribute('scroll-lock', '');
    dialog.setAttribute('aria-labelledby', modalTitleId);
    if (modalSummaryText) {
      dialog.setAttribute('aria-describedby', modalSummaryId);
    }

    const body = document.createElement('div');
    body.className = 'class-card-modal__body';

    const closeButton = document.createElement('button');
    closeButton.type = 'button';
    closeButton.className = 'button button-unstyled close-button class-card-modal__close';
    closeButton.setAttribute('aria-label', closeLabel);
    closeButton.setAttribute('data-class-card-close', '');
    closeButton.innerHTML = ICON_CLOSE;

    const layout = document.createElement('div');
    layout.className = 'class-card-modal__layout';

    const mediaContainer = document.createElement('div');
    mediaContainer.className = 'class-card-modal__media';
    mediaContainer.appendChild(
      this.#buildMediaGallery(media, dialog.id, {
        previousLabel,
        nextLabel,
        mediaAltFallback: className || instructorName || 'Class media',
        placeholderSVG,
      })
    );

    const content = document.createElement('div');
    content.className = 'class-card-modal__content';

    const header = document.createElement('header');
    header.className = 'class-card-modal__header';

    if (statusText) {
      const statusBadge = document.createElement('span');
      statusBadge.className = 'class-card-modal__status';
      if (statusColor) {
        statusBadge.style.setProperty('--class-card-status-color', statusColor);
      }
      statusBadge.textContent = statusText;
      header.appendChild(statusBadge);
    }

    const titleEl = document.createElement('h2');
    titleEl.id = modalTitleId;
    titleEl.className = 'class-card-modal__title';
    titleEl.textContent = className || instructorName || data.placeholder_label || 'Class details';
    header.appendChild(titleEl);

    if (modalSummaryText) {
      const summaryEl = document.createElement('p');
      summaryEl.id = modalSummaryId;
      summaryEl.className = 'class-card-modal__summary';
      summaryEl.textContent = modalSummaryText;
      header.appendChild(summaryEl);
    }

    content.appendChild(header);

    if (classDescriptionHTML) {
      const descriptionEl = document.createElement('div');
      descriptionEl.className = 'class-card-modal__description rte';
      descriptionEl.innerHTML = classDescriptionHTML;
      content.appendChild(descriptionEl);
    }

    const metaItems = [];
    if (date) metaItems.push({ label: dateLabel, value: date });
    if (time) metaItems.push({ label: timeLabel, value: time });
    if (room) metaItems.push({ label: locationLabel, value: room });
    if (category) metaItems.push({ label: categoryLabel, value: category });
    if (availabilityMessage) {
      metaItems.push({ label: availabilityLabel, value: availabilityMessage });
    }

    if (metaItems.length) {
      const metaList = document.createElement('ul');
      metaList.className = 'class-card-modal__meta';
      metaList.setAttribute('role', 'list');

      metaItems.forEach(({ label, value }) => {
        if (!value) return;
        const item = document.createElement('li');
        item.className = 'class-card-modal__meta-item';

        const labelEl = document.createElement('span');
        labelEl.className = 'class-card-modal__meta-label';
        labelEl.textContent = label;

        const valueEl = document.createElement('span');
        valueEl.className = 'class-card-modal__meta-value';
        valueEl.textContent = value;

        item.append(labelEl, valueEl);
        metaList.appendChild(item);
      });

      if (metaList.children.length) {
        content.appendChild(metaList);
      }
    }

    // Add register button in registration mode
    if (this.isRegistrationMode) {
      const registerButtonWrapper = document.createElement('div');
      registerButtonWrapper.className = 'class-card-modal__register-wrapper';

      const registerButton = document.createElement('button');
      registerButton.type = 'button';
      registerButton.className = 'class-card-modal__register-button button button--primary';
      registerButton.setAttribute('data-modal-register-button', '');
      registerButton.innerHTML = `
        <span class="class-card-modal__register-text">Select to Register</span>
        <svg class="class-card-modal__register-icon" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M13.3333 4L6.00004 11.3333L2.66671 8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      `;

      // Get the checkbox from the card to sync state
      const checkboxId = this.dataset.classId?.replace('://', '-').replace(/\//g, '-');
      const checkbox = checkboxId ? document.getElementById(`class-${checkboxId}`) : null;

      // Check if this class is already registered (committed to server)
      const isAlreadyRegistered = this.#isClassRegistered();
      const isClassPast = this.dataset.classPast === 'true';

      if (isAlreadyRegistered) {
        // Already registered - show locked state
        registerButton.classList.add('class-card-modal__register-button--registered');
        registerButton.querySelector('.class-card-modal__register-text').textContent = 'Registered';
        registerButton.disabled = true;
      } else if (isClassPast) {
        // Class date has passed - block new registration
        registerButton.classList.add('class-card-modal__register-button--past');
        registerButton.querySelector('.class-card-modal__register-text').textContent = 'Class Ended';
        registerButton.disabled = true;
      } else if (checkbox?.checked) {
        // Pending selection
        registerButton.classList.add('class-card-modal__register-button--selected');
        registerButton.querySelector('.class-card-modal__register-text').textContent = 'Selected';
      }

      // Toggle selection when clicked (only if not already registered and not past)
      if (!isAlreadyRegistered && !isClassPast) {
        registerButton.addEventListener('click', () => {
          if (checkbox) {
            checkbox.click(); // Trigger the existing selection logic

            // Update button visual state
            if (checkbox.checked) {
              registerButton.classList.add('class-card-modal__register-button--selected');
              registerButton.querySelector('.class-card-modal__register-text').textContent = 'Selected';
            } else {
              registerButton.classList.remove('class-card-modal__register-button--selected');
              registerButton.querySelector('.class-card-modal__register-text').textContent = 'Select to Register';
            }
          }
        });
      }

      registerButtonWrapper.appendChild(registerButton);
      content.appendChild(registerButtonWrapper);
    }

    if (requiresKit || kitProductTitle) {
      const kitWrapper = document.createElement('div');
      kitWrapper.className = 'class-card-modal__kit';

      const kitLabelEl = document.createElement('span');
      kitLabelEl.className = 'class-card-modal__kit-label';
      kitLabelEl.textContent = kitLabel;

      const kitContent = document.createElement('div');
      kitContent.className = 'class-card-modal__kit-content';

      // Main requirement message
      const kitRequirementEl = document.createElement('p');
      kitRequirementEl.className = 'class-card-modal__kit-note';
      kitRequirementEl.textContent = kitNote;
      kitContent.appendChild(kitRequirementEl);

      // Price (if available)
      if (kitProductPrice) {
        const kitPriceEl = document.createElement('p');
        kitPriceEl.className = 'class-card-modal__kit-price';
        kitPriceEl.textContent = `Kit price: ${kitProductPrice}`;
        kitContent.appendChild(kitPriceEl);
      }

      // Kit description (if available)
      if (kitProductDescription) {
        const kitDescriptionEl = document.createElement('div');
        kitDescriptionEl.className = 'class-card-modal__kit-description rte';
        kitDescriptionEl.innerHTML = kitProductDescription;
        kitContent.appendChild(kitDescriptionEl);
      }

      // Purchase note
      const kitPurchaseNoteEl = document.createElement('p');
      kitPurchaseNoteEl.className = 'class-card-modal__kit-note class-card-modal__kit-note--secondary';
      kitPurchaseNoteEl.textContent = this.dataset.kitAvailable === 'false' ? kitsClosedMessage : kitPurchaseNote;
      kitContent.appendChild(kitPurchaseNoteEl);

      kitWrapper.append(kitLabelEl, kitContent);
      content.appendChild(kitWrapper);
    }

    // Build instructor section (will be added under media gallery)
    let instructorSection = null;
    if (instructorName) {
      instructorSection = document.createElement('section');
      instructorSection.className = 'class-card-modal__instructor';
      instructorSection.setAttribute('aria-labelledby', `${modalTitleId}-instructor`);

      const sectionTitle = document.createElement('h3');
      sectionTitle.id = `${modalTitleId}-instructor`;
      sectionTitle.className = 'class-card-modal__section-title';
      sectionTitle.textContent = instructorLabel;

      const instructorContent = document.createElement('div');
      instructorContent.className = 'class-card-modal__instructor-content';

      if (instructorImage) {
        const instructorMedia = document.createElement('div');
        instructorMedia.className = 'class-card-modal__instructor-media';
        instructorMedia.innerHTML = `<img class="class-card-modal__instructor-image" src="${instructorImage}" alt="${this.#escapeAttribute(instructorName)}" loading="lazy">`;
        instructorContent.appendChild(instructorMedia);
      }

      const instructorDetails = document.createElement('div');
      instructorDetails.className = 'class-card-modal__instructor-details';

      const instructorNameEl = document.createElement('p');
      instructorNameEl.className = 'class-card-modal__instructor-name';
      instructorNameEl.textContent = instructorName;
      instructorDetails.appendChild(instructorNameEl);

      if (instructorBioHTML) {
        const instructorBioEl = document.createElement('div');
        instructorBioEl.className = 'class-card-modal__instructor-bio';
        instructorBioEl.innerHTML = instructorBioHTML;
        instructorDetails.appendChild(instructorBioEl);
      }

      instructorContent.appendChild(instructorDetails);
      instructorSection.append(sectionTitle, instructorContent);
    }

    // Add instructor under media gallery (left column)
    if (instructorSection) {
      mediaContainer.appendChild(instructorSection);
    }

    layout.append(mediaContainer, content);
    body.append(closeButton, layout);
    dialog.appendChild(body);
    dialogComponent.appendChild(dialog);
    this.appendChild(dialogComponent);

    closeButton.addEventListener('click', () => {
      if (typeof dialog.showModal !== 'function') {
        this.#closeDialogFallback();
      } else if (typeof dialogComponent.closeDialog === 'function') {
        dialogComponent.closeDialog();
      } else {
        dialog.close();
      }
    });

    this.dialogComponent = dialogComponent;
    this.dialog = dialog;
  }

  #buildMediaGallery(mediaEntries = [], modalId, { previousLabel, nextLabel, mediaAltFallback, placeholderSVG }) {
    const media = Array.isArray(mediaEntries) ? mediaEntries : [];

    if (!media.length) {
      const wrapper = document.createElement('div');
      wrapper.className = 'class-card-modal__slide class-card-modal__slide--placeholder';
      if (placeholderSVG) {
        wrapper.innerHTML = placeholderSVG;
      } else {
        wrapper.innerHTML = `<div class="class-card-modal__placeholder" aria-hidden="true"></div>`;
      }
      return wrapper;
    }

    const slideshow = document.createElement('slideshow-component');
    slideshow.className = 'class-card-modal__slideshow';
    slideshow.setAttribute('initial-slide', '0');
    if (media.length > 1) {
      slideshow.setAttribute('infinite', '');
    }

    const container = document.createElement('slideshow-container');
    container.setAttribute('ref', 'slideshowContainer');

    if (media.length > 1) {
      const arrows = document.createElement('slideshow-arrows');
      arrows.setAttribute('position', 'center');

      const previous = this.#buildArrowButton('previous', previousLabel);
      const next = this.#buildArrowButton('next', nextLabel);

      arrows.append(previous, next);
      container.appendChild(arrows);
    }

    const slides = document.createElement('slideshow-slides');
    slides.setAttribute('ref', 'scroller');
    slides.setAttribute('tabindex', '-1');

    media.forEach((item, index) => {
      if (!item) return;
      const isObject = typeof item === 'object' && item !== null;
      const url = typeof item === 'string' ? item : item.url;
      if (!url) return;
      const slide = document.createElement('slideshow-slide');
      slide.setAttribute('ref', 'slides[]');
      slide.setAttribute('slide-id', `${modalId}-media-${index + 1}`);
      slide.className = 'class-card-modal__slide';
      slide.setAttribute('aria-hidden', index === 0 ? 'false' : 'true');

      const altText = isObject && item.alt ? item.alt : mediaAltFallback || '';
      const lqip = isObject && item.lqip ? item.lqip : '';
      const srcset = isObject && item.srcset ? item.srcset : '';
      const width = isObject && item.width ? item.width : '';
      const height = isObject && item.height ? item.height : '';

      const sizesAttr = ' sizes="(min-width: 900px) 480px, 92vw"';
      const srcsetAttr = srcset ? ` srcset="${this.#escapeAttribute(srcset)}"` : '';
      const widthAttr = width ? ` width="${width}"` : '';
      const heightAttr = height ? ` height="${height}"` : '';
      const loadingAttr = index === 0 ? ' loading="eager" fetchpriority="high"' : ' loading="lazy"';
      const wrapperStyle = lqip ? ` style="background-image: url('${this.#escapeAttribute(lqip)}');"` : '';

      slide.innerHTML = `<div class="class-card-modal__image-wrapper"${wrapperStyle}><img class="class-card-modal__image" src="${url}"${srcsetAttr}${sizesAttr}${widthAttr}${heightAttr} alt="${this.#escapeAttribute(altText)}"${loadingAttr} decoding="async"></div>`;

      const img = slide.querySelector('img');
      if (img) {
        if (img.complete && img.naturalWidth > 0) {
          img.classList.add('class-card-modal__image--loaded');
        } else {
          img.addEventListener('load', () => img.classList.add('class-card-modal__image--loaded'), { once: true });
        }
      }

      slides.appendChild(slide);
    });

    container.appendChild(slides);
    slideshow.appendChild(container);

    return slideshow;
  }

  #buildArrowButton(action, label) {
    const button = document.createElement('button');
    button.className = `slideshow-control slideshow-control--${action} button button-unstyled button-unstyled--transparent slideshow-control--style-arrow${action === 'previous' ? ' flip-x' : ''}`;
    button.setAttribute('on:click', `/${action}`);
    button.setAttribute('ref', action);
    button.setAttribute('aria-label', label);

    const wrapper = document.createElement('span');
    wrapper.className = 'svg-wrapper icon-arrow';
    wrapper.innerHTML = ICON_ARROW;

    button.appendChild(wrapper);
    return button;
  }

  #escapeAttribute(value) {
    if (value == null) return '';
    return String(value).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/'/g, '&#39;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  #escapeHtml(value) {
    if (value == null) return '';
    return this.#escapeAttribute(value).replace(/\n/g, '<br>');
  }

  #toNumber(value) {
    if (value === null || value === undefined || value === '') return null;
    const number = Number(value);
    return Number.isNaN(number) ? null : number;
  }

  #buildAvailabilityMessage(count, limit) {
    // Seat counts hidden - only show Available/Full status badge on card
    return '';
  }

  /**
   * Checks if this class is already registered (committed to server)
   * @returns {boolean} True if this class is in the user's registered classes
   */
  #isClassRegistered() {
    const classId = this.dataset.classId;
    const classDate = this.dataset.classDate;
    const classTime = this.dataset.classTime;

    if (!classId) return false;

    // Check registration manager for committed registrations
    const manager = window.classRegistrationManager;
    if (!manager?.tickets || !manager.state?.selectedTicketId) return false;

    const ticketRecord = manager.findTicketById(manager.state.selectedTicketId);
    if (!ticketRecord?.selected_classes) return false;

    // Check if this class is in the registered classes
    return ticketRecord.selected_classes.some(cls =>
      cls.class_id === classId &&
      cls.date === classDate &&
      cls.time_slot === classTime
    );
  }
}

if (!customElements.get('class-card-component')) {
  customElements.define('class-card-component', ClassCardComponent);
}
